import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ScienceIcon from '@mui/icons-material/Science';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SuperAdminLayout from './SuperAdminLayout';
import StatCard from './StatCard';
import CompanyDetailsModal from './CompanyDetailsModal';
import SuperAdminService, { type FiscalDeviceWithCompany, type SuperAdminReminder } from '../../services/SuperAdminService';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../accounting/shared/StatsCard';
import { buildReminderHref } from './superAdminReminderLink';
import { reminderTiming } from '../../contexts/SuperAdminRemindersContext';
import { isUsageProductModule, usageModuleLabel } from './usageModuleLabels';
import { downloadDashboardReport } from './exportDashboardReport';

const formatCount = (value: number) => value.toLocaleString('tr-TR');

// ============================================================================
// WIDGET YÖNETİMİ
// ----------------------------------------------------------------------------
// Süperadmin dashboard'u bölümlere (widget) ayrılır; sağ üstteki menüden her biri
// gösterilip gizlenebilir. Tercih localStorage'da kalıcıdır (kullanıcı/tarayıcı bazlı).
// ============================================================================
type WidgetId = 'online' | 'kpis' | 'miniLists' | 'trend' | 'actions' | 'usage' | 'deadlines';

const WIDGET_DEFS: { id: WidgetId; label: string }[] = [
  { id: 'online', label: 'Şu An Online' },
  { id: 'kpis', label: 'Özet Kartları' },
  { id: 'miniLists', label: 'Mini Listeler' },
  { id: 'trend', label: 'Giriş Trafiği Grafiği' },
  { id: 'actions', label: 'Aksiyon Gerektirenler' },
  { id: 'usage', label: 'İstek Bazlı Detay' },
  { id: 'deadlines', label: 'Süre Takibi' },
];

const WIDGET_STORAGE_KEY = 'superadmin-dashboard-widgets-v1';

const defaultWidgetVisibility = (): Record<WidgetId, boolean> =>
  WIDGET_DEFS.reduce((acc, w) => {
    acc[w.id] = true;
    return acc;
  }, {} as Record<WidgetId, boolean>);

const loadWidgetVisibility = (): Record<WidgetId, boolean> => {
  const defaults = defaultWidgetVisibility();
  try {
    const raw = localStorage.getItem(WIDGET_STORAGE_KEY);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as Partial<Record<WidgetId, boolean>>) };
  } catch {
    /* bozuk değer → varsayılan */
  }
  return defaults;
};

// ============================================================================
// SUPERADMIN DASHBOARD
// ============================================================================
// Asıl amaç: TRAFİĞİ görmek — şirketler sistemi kullanıyor mu?
//  - Kullanım özeti (bugün / son 7 gün / son 30 gün aktif + kullanmayan)
//  - En aktif şirketler (giriş sayısına göre)
//  - Sistemi kullanmayan şirketler (son 30 günde hiç giriş yok) → en kritik gösterge
//  - Süresi yaklaşan/dolan yazarkasalar, demolar ve sözleşmeler
// Tüm veriler mevcut süperadmin uçlarından türetilir; backend değişikliği yok.
// ============================================================================

interface DashboardCompany {
  id: string;
  name: string;
  logo?: string;
  admin: { name: string; email: string; phone?: string };
  maxPersonnel?: number;
  createdAt?: string;
  demoUntil?: string | null;
  demoExpired?: boolean;
}

interface CompanyActivity {
  companyId: string;
  lastLogin: number; // epoch-ms
  loginCount: number; // kümülatif (tüm zamanlar)
  recentLoginCount?: number; // son 30 gün (LoginActivityLogs)
  lastPlatform?: string;
}

interface TrendPoint {
  date: string; // yyyy-MM-dd
  total: number;
  uniqueCompanies: number;
  uniqueUsers: number;
  web: number;
  mobile: number;
  other: number;
}

interface TodayUsageCompany {
  companyId: string;
  companyName: string;
  requests: number;
  users: number;
}

interface UsageSummary {
  dau: number;
  wau: number;
  mau: number;
  companiesToday: number;
  todayCompanies?: TodayUsageCompany[];
  companies7: number;
  companies30: number;
  last7Requests: number;
  prev7Requests: number;
  wowChangePct: number | null;
  daily: { date: string; requests: number; uniqueUsers: number; uniqueCompanies: number }[];
  hourly: { hour: number; count: number }[];
}

interface OnlineEntry {
  firebaseUid: string;
  displayName?: string | null;
  email?: string | null;
  companyId: string;
  companyName?: string | null;
  role?: string | null;
  platform: string;
  module: string;
  secondsAgo: number;
  /** Son istek anı (UTC ISO). Yoksa polling anından secondsAgo ile türetilir. */
  lastSeenUtc?: string | null;
}

interface ModuleSummary {
  module: string;
  totalRequests: number;
  uniqueCompanies: number;
}

interface CompanyModuleRow {
  companyId: string;
  companyName: string;
  totalRequests: number;
  modules: Record<string, number>;
}

interface DecliningCompany {
  companyId: string;
  companyName: string;
  recentDailyAvg: number;
  previousDailyAvg: number;
  recentTotal: number;
  previousTotal: number;
  dropPct: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "3 gün önce" / "Az önce" / "Hiç giriş yok". */
const timeAgo = (timestamp?: number): string => {
  if (!timestamp) return 'Hiç giriş yok';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / DAY_MS);
  if (days > 0) return `${days} gün önce`;
  if (hours > 0) return `${hours} saat önce`;
  if (minutes > 0) return `${minutes} dakika önce`;
  return 'Az önce';
};

/** Bitişe kalan gün (negatif = geçmiş). */
const daysUntil = (dateStr?: string | null): number | null => {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / DAY_MS);
};

const formatDateTR = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  } catch {
    return '-';
  }
};

const ONLINE_ROLE_TR: Record<string, string> = {
  admin: 'Admin',
  subadmin: 'Alt Admin',
  manager: 'Yönetici',
  employee: 'Personel',
  courier: 'Kurye',
  superadmin: 'Süper Admin',
  dealer: 'Bayi',
  owner: 'Sahip',
};

const onlineRoleLabel = (role?: string | null) => {
  if (!role) return '—';
  return ONLINE_ROLE_TR[role.trim().toLowerCase()] || role;
};

/** Ortak numaralandırılmış liste kartı. */
const NumberedListCard: React.FC<{
  title: string;
  color: string;
  count: number;
  emptyText: string;
  children: React.ReactNode;
  maxHeight?: number;
}> = ({ title, color, count, emptyText, children, maxHeight = 380 }) => (
  <Paper elevation={1} sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
        {title}
      </Typography>
      <Chip label={count} size="small" sx={{ bgcolor: `${color}15`, color, fontWeight: 700 }} />
    </Box>
    {count === 0 ? (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">{emptyText}</Typography>
      </Box>
    ) : (
      <List dense sx={{ overflowY: 'auto', maxHeight, py: 0 }}>
        {children}
      </List>
    )}
  </Paper>
);

/** Sıra numarası rozeti. */
const RankBadge: React.FC<{ index: number; color?: string }> = ({ index, color = '#1976d2' }) => (
  <Box
    sx={{
      minWidth: 26,
      height: 26,
      borderRadius: '50%',
      bgcolor: `${color}15`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8rem',
      fontWeight: 700,
      mr: 1,
    }}
  >
    {index + 1}
  </Box>
);

const MINI_LIST_LIMIT = 8;

interface MiniListRow {
  id: string;
  primary: string;
  secondary?: string;
  value: string;
  valueColor?: string;
}

/** Muhasebe dashboard'daki mini tablo (SN / ad / değer). */
const MiniListWidget: React.FC<{
  title: string;
  nameColumn: string;
  valueColumn: string;
  rows: MiniListRow[];
  emptyText: string;
  onRowClick?: (id: string) => void;
  onViewAll?: () => void;
}> = ({ title, nameColumn, valueColumn, rows, emptyText, onRowClick, onViewAll }) => (
  <Card
    sx={{
      height: '100%',
      minHeight: 320,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <CardHeader
      title={title}
      titleTypographyProps={{ fontWeight: 'bold', fontSize: '1rem' }}
      sx={{ py: 1, px: 1.5, '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } }}
      action={
        onViewAll ? (
          <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto' }}>
            Tümü
          </Button>
        ) : undefined
      }
    />
    <Divider />
    <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          {emptyText}
        </Typography>
      ) : (
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 40, px: 1 }}>SN</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{nameColumn}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{valueColumn}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, MINI_LIST_LIMIT).map((row, index) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={onRowClick ? () => onRowClick(row.id) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  <TableCell sx={{ width: 40, px: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ py: 0.75, maxWidth: 160 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {row.primary}
                    </Typography>
                    {row.secondary && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {row.secondary}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: row.valueColor || 'success.main', fontSize: '0.8rem' }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardContent>
  </Card>
);

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<DashboardCompany[]>([]);
  const [activities, setActivities] = useState<CompanyActivity[]>([]);
  const [reminders, setReminders] = useState<SuperAdminReminder[]>([]);
  const [pendingDeletions, setPendingDeletions] = useState<number>(0);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  // İstek bazlı kullanım telemetrisi
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [online, setOnline] = useState<OnlineEntry[]>([]);
  const [moduleSummary, setModuleSummary] = useState<ModuleSummary[]>([]);
  const [companyModules, setCompanyModules] = useState<CompanyModuleRow[]>([]);
  const [declining, setDeclining] = useState<DecliningCompany[]>([]);
  const [fiscalDevices, setFiscalDevices] = useState<FiscalDeviceWithCompany[]>([]);
  const [fiscalDeviceCount, setFiscalDeviceCount] = useState(0);
  const [onlineOpen, setOnlineOpen] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Widget görünürlüğü (sağ üst menü) — localStorage'da kalıcı.
  const [visibleWidgets, setVisibleWidgets] = useState<Record<WidgetId, boolean>>(loadWidgetVisibility);
  const [widgetMenuAnchor, setWidgetMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(visibleWidgets));
    } catch {
      /* kota/erişim hatası sessiz geçilir */
    }
  }, [visibleWidgets]);

  const toggleWidget = (id: WidgetId) =>
    setVisibleWidgets((prev) => ({ ...prev, [id]: !prev[id] }));

  const resetWidgets = () => setVisibleWidgets(defaultWidgetVisibility());

  const hiddenWidgetCount = WIDGET_DEFS.filter((w) => !visibleWidgets[w.id]).length;

  const [detailsCompany, setDetailsCompany] = useState<DashboardCompany | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const service = SuperAdminService.getInstance();
        const [companyList, activityRes, reminderList, deletionRes, trendRes, usageRes, modulesRes, decliningRes, fiscalRes] = await Promise.all([
          service.getAllCompanies(),
          service.getRecentLoginActivities(),
          service.getSuperAdminReminders(30).catch(() => [] as SuperAdminReminder[]),
          service.getDeletionRequests().catch(() => null),
          service.getLoginTrend(30).catch(() => null),
          service.getUsageSummary(30).catch(() => null),
          service.getUsageModules(30).catch(() => null),
          service.getUsageDeclining().catch(() => null),
          service.getFiscalDevicesPaged({ page: 1, pageSize: 50 }).catch(() => ({ items: [], totalCount: 0 })),
        ]);

        setCompanies(companyList as DashboardCompany[]);

        const activityData: CompanyActivity[] =
          activityRes?.success && Array.isArray(activityRes.data) ? activityRes.data : [];
        setActivities(activityData);

        if (usageRes?.success) setUsage(usageRes as UsageSummary);
        if (modulesRes?.success) {
          const rawModules: ModuleSummary[] = Array.isArray(modulesRes.modules) ? modulesRes.modules : [];
          setModuleSummary(rawModules.filter((m) => isUsageProductModule(m.module)));
          setCompanyModules(Array.isArray(modulesRes.companies) ? modulesRes.companies : []);
        }
        if (decliningRes?.success && Array.isArray(decliningRes.data)) {
          setDeclining(decliningRes.data as DecliningCompany[]);
        }

        const fiscalItems = Array.isArray(fiscalRes?.items) ? fiscalRes.items : [];
        const recentFiscal = [...fiscalItems].sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setFiscalDevices(recentFiscal);
        setFiscalDeviceCount(typeof fiscalRes?.totalCount === 'number' ? fiscalRes.totalCount : recentFiscal.length);

        setReminders(reminderList);

        if (trendRes?.success && Array.isArray(trendRes.data)) {
          setTrend(trendRes.data as TrendPoint[]);
        }

        if (deletionRes?.requests) {
          setPendingDeletions(deletionRes.requests.filter((r) => r.status === 'pending').length);
        }
      } catch (error) {
        console.error('❌ [Dashboard] Veriler yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // "Şu an online" — 60 sn'de bir hafif polling (dashboard açıkken).
  useEffect(() => {
    const service = SuperAdminService.getInstance();
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await service.getUsageOnline(180);
        if (cancelled || !res?.success) return;
        const receivedAt = Date.now();
        const rawRows = Array.isArray(res.data) ? res.data : Array.isArray(res.Data) ? res.Data : [];
        const rows: OnlineEntry[] = rawRows.map((raw: any) => ({
          firebaseUid: String(raw.firebaseUid ?? raw.FirebaseUid ?? ''),
          displayName: raw.displayName ?? raw.DisplayName ?? null,
          email: raw.email ?? raw.Email ?? null,
          companyId: String(raw.companyId ?? raw.CompanyId ?? '').trim(),
          companyName: raw.companyName ?? raw.CompanyName ?? null,
          role: raw.role ?? raw.Role ?? null,
          platform: String(raw.platform ?? raw.Platform ?? ''),
          module: String(raw.module ?? raw.Module ?? ''),
          secondsAgo: Number(raw.secondsAgo ?? raw.SecondsAgo ?? 0),
          lastSeenUtc: raw.lastSeenUtc ?? raw.LastSeenUtc ?? null,
        }));
        setOnline(
          rows.map((o) => ({
            ...o,
            lastSeenUtc:
              o.lastSeenUtc ||
              new Date(receivedAt - Math.max(0, o.secondsAgo || 0) * 1000).toISOString(),
          }))
        );
      } catch {
        /* sessiz geç */
      }
    };
    poll();
    const id = window.setInterval(poll, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const onlineGroups = useMemo(() => {
    const nameById = new Map<string, string>();
    companies.forEach((c) => {
      if (c.id && c.name) nameById.set(c.id, c.name);
    });
    const map = new Map<string, { id: string; name: string; users: OnlineEntry[] }>();
    online.forEach((o) => {
      const role = (o.role || '').trim().toLowerCase();
      if (role === 'superadmin') return;
      const id = (o.companyId || '').trim();
      if (!id) return;
      const name = (o.companyName || '').trim() || nameById.get(id) || id;
      const existing = map.get(id);
      if (existing) existing.users.push(o);
      else map.set(id, { id, name, users: [o] });
    });
    return [...map.values()].sort(
      (a, b) => b.users.length - a.users.length || a.name.localeCompare(b.name, 'tr')
    );
  }, [online, companies]);

  const onlineRows = useMemo(() => {
    const platformLabel = (platform: string) => {
      const p = platform.trim().toLowerCase();
      if (p === 'web') return 'Web';
      if (p === 'mobile' || p.startsWith('mobile')) return 'Mobil';
      if (!p || p === 'other') return 'Diğer';
      return platform;
    };
    return onlineGroups
      .flatMap((group) =>
        group.users.map((user) => {
          const person = user.displayName || (user.role ? onlineRoleLabel(user.role) : 'Bilinmeyen kişi');
          const email = (user.email || '').trim();
          return {
            key: `${group.id}:${user.firebaseUid}`,
            person,
            // Kişi adı zaten e-posta ise tekrar gösterme.
            email: email && email.toLowerCase() !== person.trim().toLowerCase() ? email : '',
            role: onlineRoleLabel(user.role),
            companyId: group.id,
            companyName: group.name,
            module: usageModuleLabel(user.module),
            platform: platformLabel(user.platform),
            secondsAgo: user.secondsAgo || 0,
          };
        })
      )
      .sort(
        (a, b) =>
          a.secondsAgo - b.secondsAgo || a.companyName.localeCompare(b.companyName, 'tr') || a.person.localeCompare(b.person, 'tr')
      );
  }, [onlineGroups]);

  // Şirket bazlı son giriş haritası
  const activityMap = useMemo(() => {
    const map: Record<string, CompanyActivity> = {};
    activities.forEach((a) => {
      if (a.companyId) map[a.companyId] = a;
    });
    return map;
  }, [activities]);

  // Trafik hesaplamaları
  const traffic = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * DAY_MS;

    let active7 = 0;
    let active30 = 0;

    // Aktivite kaydı olan (son 30 gün içinde giriş) şirketler
    Object.values(activityMap).forEach((a) => {
      active30 += 1;
      if (a.lastLogin >= sevenDaysAgo) active7 += 1;
    });

    // Kullanmayan: son 30 günde recent listesinde yer almayan şirketler
    const dormant = companies.filter((c) => !activityMap[c.id]);

    // En aktif: son 30 günün giriş sayısına göre (kümülatif loginCount eski şirketleri kayırırdı).
    // recentLoginCount yoksa (backend eski) kümülatif sayaca düşülür.
    const recentCount = (id: string) =>
      activityMap[id]?.recentLoginCount ?? activityMap[id]?.loginCount ?? 0;
    const mostActive = [...companies]
      .filter((c) => activityMap[c.id])
      .sort((a, b) => recentCount(b.id) - recentCount(a.id));

    // Son 7 gün aktif şirketler (son girişe göre)
    const recentlyActive = [...companies]
      .filter((c) => activityMap[c.id] && activityMap[c.id].lastLogin >= sevenDaysAgo)
      .sort((a, b) => (activityMap[b.id]?.lastLogin || 0) - (activityMap[a.id]?.lastLogin || 0));

    const active30List = [...companies]
      .filter((c) => !!activityMap[c.id])
      .sort((a, b) => (activityMap[b.id]?.lastLogin || 0) - (activityMap[a.id]?.lastLogin || 0));

    return { active7, active30, dormant, mostActive, recentlyActive, active30List };
  }, [companies, activityMap]);

  // Demo şirketler — dolanlar/dolmak üzere olanlar üstte
  const demoCompanies = useMemo(() => {
    return companies
      .filter((c) => !!c.demoUntil)
      .map((c) => ({ company: c, days: daysUntil(c.demoUntil) ?? 9999 }))
      .sort((a, b) => a.days - b.days);
  }, [companies]);

  // Trafik grafiği için gün etiketli seri + dönem özeti
  const trendChart = useMemo(() => {
    const data = trend.map((p) => {
      const d = new Date(`${p.date}T00:00:00`);
      const label = Number.isNaN(d.getTime())
        ? p.date
        : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
      return { ...p, label };
    });
    const totalLogins = trend.reduce((s, p) => s + p.total, 0);
    const totalWeb = trend.reduce((s, p) => s + p.web, 0);
    const totalMobile = trend.reduce((s, p) => s + p.mobile, 0);
    const totalOther = trend.reduce((s, p) => s + p.other, 0);
    const peak = trend.reduce((m, p) => (p.total > m ? p.total : m), 0);
    return { data, totalLogins, totalWeb, totalMobile, totalOther, peak };
  }, [trend]);

  // İstek bazlı kullanım grafikleri (gün etiketli seri + saatlik + modül ısı haritası ölçeği)
  const usageCharts = useMemo(() => {
    const daily = (usage?.daily ?? []).map((p) => {
      const d = new Date(`${p.date}T00:00:00`);
      const label = Number.isNaN(d.getTime())
        ? p.date
        : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
      return { ...p, label };
    });
    const hourly = (usage?.hourly ?? []).map((h) => ({
      label: `${h.hour.toString().padStart(2, '0')}`,
      count: h.count,
    }));
    // Isı haritası: tüm iş modülleri kolon olur. Sığmayanlar yatay kaydırılır.
    const topModules = moduleSummary.map((m) => m.module);
    let maxCell = 0;
    companyModules.forEach((c) => {
      topModules.forEach((m) => {
        const v = c.modules[m] || 0;
        if (v > maxCell) maxCell = v;
      });
    });
    return { daily, hourly, topModules, maxCell };
  }, [usage, moduleSummary, companyModules]);

  const fiscalReminders = useMemo(
    () => reminders.filter((r) => r.kind === 'FISCAL_DEVICE'),
    [reminders]
  );
  // Sözleşme/abonelik + ürünler tek "BILLING" kartında birleşti (eski CONTRACT dahil).
  const contractReminders = useMemo(
    () => reminders.filter((r) => r.kind === 'BILLING' || r.kind === 'CONTRACT'),
    [reminders]
  );

  const openDetails = (company: DashboardCompany) => {
    setDetailsCompany(company);
    setDetailsOpen(true);
  };

  const goToReminder = (reminder: SuperAdminReminder) => {
    navigate(buildReminderHref(reminder));
  };

  const fiscalBranchLabel = (device: FiscalDeviceWithCompany) =>
    device.branch || device.legacyBranch || device.usageArea || 'Şube yok';

  const downloadReport = async () => {
    try {
      setExporting(true);
      await downloadDashboardReport({
        companies,
        activities,
        usage,
        trend,
        moduleSummary,
        companyModules,
        declining,
        online,
      });
    } catch (error) {
      console.error('❌ [Dashboard] Rapor indirilemedi:', error);
    } finally {
      setExporting(false);
    }
  };

  const openCompanyById = (companyId: string) => {
    const company = companies.find((c) => c.id === companyId);
    if (company) openDetails(company);
  };

  const recentCompanyRows: MiniListRow[] = useMemo(() => {
    const recentCount = (id: string) =>
      activityMap[id]?.recentLoginCount ?? activityMap[id]?.loginCount ?? 0;
    return [...companies]
      .sort((a, b) => {
        const aLogin = activityMap[a.id]?.lastLogin || 0;
        const bLogin = activityMap[b.id]?.lastLogin || 0;
        if (bLogin !== aLogin) return bLogin - aLogin;
        return recentCount(b.id) - recentCount(a.id);
      })
      .map((company) => {
        const act = activityMap[company.id];
        return {
          id: company.id,
          primary: company.name,
          secondary: act ? timeAgo(act.lastLogin) : `Kayıt: ${formatDateTR(company.createdAt)}`,
          value: act ? `${act.recentLoginCount ?? act.loginCount} giriş` : 'Giriş yok',
          valueColor: act ? '#2e7d32' : '#9e9e9e',
        };
      });
  }, [companies, activityMap]);

  const todayUsageCompanies = useMemo(() => {
    const map = new Map<string, { id: string; name: string; requests: number; users: number }>();
    (usage?.todayCompanies ?? []).forEach((c) => {
      if (!c.companyId) return;
      map.set(c.companyId, {
        id: c.companyId,
        name: c.companyName || c.companyId,
        requests: c.requests || 0,
        users: c.users || 0,
      });
    });
    onlineGroups.forEach((g) => {
      if (!g.id || g.id === '—') return;
      const existing = map.get(g.id);
      if (existing) existing.users = Math.max(existing.users, g.users.length);
      else map.set(g.id, { id: g.id, name: g.name, requests: 0, users: g.users.length });
    });
    return [...map.values()].sort((a, b) => b.requests - a.requests || b.users - a.users || a.name.localeCompare(b.name, 'tr'));
  }, [usage, onlineGroups]);

  const todayActiveRows: MiniListRow[] = useMemo(
    () =>
      todayUsageCompanies.map((company) => ({
        id: company.id,
        primary: company.name,
        secondary: `${company.users} kullanıcı`,
        value: company.requests > 0 ? `${company.requests.toLocaleString('tr-TR')} istek` : 'Şu an çevrimiçi',
        valueColor: '#7b1fa2',
      })),
    [todayUsageCompanies]
  );

  const demoRows: MiniListRow[] = useMemo(
    () =>
      demoCompanies.map(({ company, days }) => ({
        id: company.id,
        primary: company.name,
        secondary: `Bitiş: ${formatDateTR(company.demoUntil)}`,
        value: days < 0 ? `${Math.abs(days)} gün geçti` : days === 0 ? 'Bugün doluyor' : `${days} gün`,
        valueColor: days < 0 ? '#d32f2f' : days <= 7 ? '#ed6c02' : '#2e7d32',
      })),
    [demoCompanies]
  );

  const fiscalRows: MiniListRow[] = useMemo(
    () =>
      [...fiscalDevices]
        .sort((a, b) => (daysUntil(a.expiryDate) ?? 99999) - (daysUntil(b.expiryDate) ?? 99999))
        .map((device) => {
        const days = daysUntil(device.expiryDate);
        const bitis = device.expiryDate ? `Bitiş: ${formatDateTR(device.expiryDate)}` : fiscalBranchLabel(device);
        return {
          id: String(device.id),
          primary: device.companyName || device.companyId || 'Şirket yok',
          secondary: [device.serialNumber, bitis].filter(Boolean).join(' · '),
          value:
            days == null
              ? 'Süre yok'
              : days < 0
                ? `${Math.abs(days)} gün geçti`
                : days === 0
                  ? 'Bugün doluyor'
                  : `${days} gün`,
          valueColor: days == null ? '#9e9e9e' : days < 0 ? '#d32f2f' : days <= 7 ? '#ed6c02' : '#2e7d32',
        };
      }),
    [fiscalDevices]
  );

  const openFiscalCollection = (id: string) => {
    const device = fiscalDevices.find((d) => String(d.id) === id);
    if (!device?.companyId) return;
    const params = new URLSearchParams({
      prefill: 'FISCAL_DEVICE',
      companyId: device.companyId,
      key: `fiscal:${device.id}`,
      title: device.companyName || device.serialNumber || 'Yazarkasa',
    });
    if (device.expiryDate) params.set('expiry', device.expiryDate.slice(0, 10));
    navigate(`/superadmin/billing?${params.toString()}`);
  };

  if (loading) {
    return (
      <SuperAdminLayout title="Dashboard">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Trafik verileri yükleniyor...
          </Typography>
        </Box>
      </SuperAdminLayout>
    );
  }

  const totalCompanies = companies.length;
  const demoCount = demoCompanies.length;
  const expiredFiscal = fiscalReminders.filter((r) => r.daysRemaining < 0).length;

  return (
    <SuperAdminLayout title="Dashboard">
      {/* Sağ üst: widget yönetimi — kartları göster/gizle (tercih kalıcı) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Kim ne kullanıyor, en aktif şirketler, günlük / haftalık / aylık özet">
          <span>
            <Button
              onClick={downloadReport}
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              variant="contained"
              size="small"
              disabled={exporting}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {exporting ? 'Hazırlanıyor...' : 'Rapor İndir'}
            </Button>
          </span>
        </Tooltip>
        <Button
          onClick={(e) => setWidgetMenuAnchor(e.currentTarget)}
          startIcon={<DashboardCustomizeIcon />}
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Widget Yönetimi
          {hiddenWidgetCount > 0 && (
            <Chip
              label={`${hiddenWidgetCount} gizli`}
              size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#ed6c0220', color: '#ed6c02' }}
            />
          )}
        </Button>
        <Menu
          anchorEl={widgetMenuAnchor}
          open={Boolean(widgetMenuAnchor)}
          onClose={() => setWidgetMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 280, maxWidth: '90vw', mt: 0.5 } }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700 }}>
            Görünecek Kartlar
          </Typography>
          {WIDGET_DEFS.map((w) => (
            <MenuItem key={w.id} onClick={() => toggleWidget(w.id)} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={visibleWidgets[w.id]}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText primary={w.label} />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={() => { resetWidgets(); }} dense disabled={hiddenWidgetCount === 0}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Tümünü Göster" />
          </MenuItem>
        </Menu>
      </Box>

      {/* Şu an online — canlı istek atan kullanıcılar (60 sn polling) */}
      {visibleWidgets.online && (
      <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
        <Box
          onClick={() => setOnlineOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: onlineGroups.length > 0 ? '#2e7d32' : '#bdbdbd',
              boxShadow: onlineGroups.length > 0 ? '0 0 0 4px #2e7d3233' : 'none',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Şu An Online
          </Typography>
          <Chip
            label={`${onlineGroups.reduce((n, g) => n + g.users.length, 0)} kullanıcı`}
            color="success"
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Chip label={`${onlineGroups.length} şirket`} size="small" variant="outlined" />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
          >
            Son 3 dakikada istek atanlar · 60 sn'de güncellenir
          </Typography>
        </Box>
        <Collapse in={onlineOpen} timeout={300} unmountOnExit>
          <Box sx={{ mt: 1.5 }}>
            {onlineRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 0.5 }}>
                Son 3 dakikada istek atan şirket yok.
              </Typography>
            ) : (
              <>
                <List dense disablePadding sx={{ display: { xs: 'block', md: 'none' }, maxHeight: 420, overflow: 'auto' }}>
                  {onlineRows.map((row, index) => (
                    <ListItemButton
                      key={row.key}
                      onClick={() => openCompanyById(row.companyId)}
                      sx={{
                        alignItems: 'flex-start',
                        py: 1,
                        px: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 28,
                          pt: 0.25,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <ListItemText
                        primary={row.person}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" display="block" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.companyName}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal' }}>
                              {[row.module, row.role !== '—' ? row.role : '', row.email, row.platform, `${row.secondsAgo} sn önce`]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          </>
                        }
                        primaryTypographyProps={{ fontWeight: 700, sx: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 48 }}>SN</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Kullanıcı</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Şirket</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Modül</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                          Son istek
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {onlineRows.map((row, index) => (
                        <TableRow
                          key={row.key}
                          hover
                          onClick={() => openCompanyById(row.companyId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{index + 1}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.person}
                            </Typography>
                            {(row.role !== '—' || row.email) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                              >
                                {[row.role !== '—' ? row.role : '', row.email, row.platform]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.companyName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.module}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main', whiteSpace: 'nowrap' }}>
                            {row.secondsAgo} sn
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>
      )}

      {/* Özet kartları — muhasebe dashboard KPI tasarımı */}
      {visibleWidgets.kpis && (
      <StatsCardsContainer>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<BusinessIcon />}
            color={STATS_COLORS.blue}
            title={formatCount(totalCompanies)}
            subtitle="Toplam Şirket"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<PeopleAltIcon />}
            color={STATS_COLORS.purple}
            title={formatCount(todayUsageCompanies.length)}
            subtitle="Bugün Aktif Şirket"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<ScienceIcon />}
            color={STATS_COLORS.green}
            title={formatCount(demoCount)}
            subtitle="Demo Şirketler"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<PointOfSaleIcon />}
            color={STATS_COLORS.orange}
            title={formatCount(fiscalDeviceCount)}
            subtitle="Kayıtlı Yazarkasa"
          />
        </Grid>
      </StatsCardsContainer>
      )}

      {/* Mini listeler — kartların altındaki 4 küçük tablo */}
      {visibleWidgets.miniLists && (
      <Grid container spacing={1} sx={{ mb: 3 }} alignItems="stretch">
        <Grid item xs={12} sm={6} lg={3}>
          <MiniListWidget
            title="Şirketler"
            nameColumn="Şirket"
            valueColumn="Giriş"
            rows={recentCompanyRows}
            emptyText="Şirket yok."
            onRowClick={openCompanyById}
            onViewAll={() => navigate('/superadmin')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniListWidget
            title="Bugün Aktif Şirketler"
            nameColumn="Şirket"
            valueColumn="Giriş"
            rows={todayActiveRows}
            emptyText="Bugün istek atan şirket yok."
            onRowClick={openCompanyById}
            onViewAll={() => navigate('/superadmin')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniListWidget
            title="Demo Şirketler"
            nameColumn="Şirket"
            valueColumn="Süre"
            rows={demoRows}
            emptyText="Demo şirket yok."
            onRowClick={openCompanyById}
            onViewAll={() => navigate('/superadmin?tab=demo')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MiniListWidget
            title="Son Yazarkasalar"
            nameColumn="Şirket / Şube"
            valueColumn="Süre"
            rows={fiscalRows}
            emptyText="Kayıtlı yazarkasa yok."
            onRowClick={openFiscalCollection}
            onViewAll={() => navigate('/superadmin?tab=fiscal-devices')}
          />
        </Grid>
      </Grid>
      )}
      {pendingDeletions > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
          <Box component="span" sx={{ color: 'warning.main', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/superadmin/deletion-requests')}>
            {pendingDeletions} bekleyen silme talebi
          </Box>
        </Typography>
      )}

      {/* Günlük trafik grafiği — LoginActivityLogs (her giriş ayrı satır) */}
      {visibleWidgets.trend && trendChart.data.length > 0 && (
        <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
              Günlük Giriş Trafiği (Son 30 Gün)
            </Typography>
            <Chip label={`Toplam ${trendChart.totalLogins} giriş`} size="small" sx={{ bgcolor: '#10264815', color: '#102648', fontWeight: 700 }} />
            <Chip label={`En yoğun gün: ${trendChart.peak}`} size="small" variant="outlined" />
          </Box>
          <Box sx={{ width: '100%', height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendChart.data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="trafficFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => [value, name]}
                  labelFormatter={(label) => `Tarih: ${label}`}
                />
                <Area type="monotone" dataKey="total" name="Toplam giriş" stroke="#1976d2" strokeWidth={2} fill="url(#trafficFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>

          {/* Platform kırılımı */}
          <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Platform bazında günlük giriş
              </Typography>
              <Box sx={{ width: '100%', height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendChart.data} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                    <RechartsTooltip labelFormatter={(label) => `Tarih: ${label}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="web" name="Web" stackId="p" fill="#0288d1" />
                    <Bar dataKey="mobile" name="Mobil" stackId="p" fill="#2e7d32" />
                    <Bar dataKey="other" name="Diğer" stackId="p" fill="#9e9e9e" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Dönem toplamı (platform)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#0288d1' }} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>Web</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{trendChart.totalWeb}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#2e7d32' }} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>Mobil</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{trendChart.totalMobile}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#9e9e9e' }} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>Diğer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{trendChart.totalOther}</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Her başarılı giriş bir olay olarak sayılır (login/google/apple). Gün kovaları Türkiye saatine göredir.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Aksiyon gerektirenler — kullanmayan + düşen + en aktif (günlük bakışın asıl değeri) */}
      {visibleWidgets.actions && (
      <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Aksiyon Gerektirenler
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Kullanmayan şirketler — asıl gösterge */}
        <Grid item xs={12} md={6} lg={4}>
          <NumberedListCard
            title="Sistemi Kullanmayan Şirketler"
            color="#d32f2f"
            count={traffic.dormant.length}
            emptyText="Tüm şirketler son 30 günde aktif."
          >
            {traffic.dormant.map((company, index) => (
              <ListItemButton key={company.id} onClick={() => openDetails(company)} divider>
                <RankBadge index={index} color="#d32f2f" />
                <ListItemText
                  primary={company.name}
                  secondary={`Kayıt: ${formatDateTR(company.createdAt)} • 30+ gün giriş yok`}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                  secondaryTypographyProps={{ color: 'error.main' }}
                />
              </ListItemButton>
            ))}
          </NumberedListCard>
        </Grid>

        {/* Kullanımı düşen şirketler — churn erken uyarı */}
        <Grid item xs={12} md={6} lg={4}>
          <NumberedListCard
            title="Kullanımı Düşen Şirketler (Churn Riski)"
            color="#ed6c02"
            count={declining.length}
            emptyText="Belirgin kullanım düşüşü olan şirket yok."
          >
            {declining.map((d, index) => (
              <ListItemButton key={d.companyId} onClick={() => openCompanyById(d.companyId)} divider>
                <RankBadge index={index} color="#ed6c02" />
                <ListItemText
                  primary={d.companyName}
                  secondary={`Günlük ort. ${d.previousDailyAvg} → ${d.recentDailyAvg} istek`}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                />
                <Chip label={`-%${d.dropPct}`} size="small" color="error" />
              </ListItemButton>
            ))}
          </NumberedListCard>
        </Grid>

        {/* En aktif şirketler — son 30 gün */}
        <Grid item xs={12} md={12} lg={4}>
          <NumberedListCard
            title="En Aktif Şirketler (Son 30 Gün)"
            color="#2e7d32"
            count={traffic.mostActive.length}
            emptyText="Henüz giriş verisi yok."
          >
            {traffic.mostActive.slice(0, 20).map((company, index) => {
              const act = activityMap[company.id];
              const recent = act?.recentLoginCount ?? act?.loginCount ?? 0;
              return (
                <ListItemButton key={company.id} onClick={() => openDetails(company)} divider>
                  <RankBadge index={index} color="#2e7d32" />
                  <ListItemText
                    primary={company.name}
                    secondary={`${timeAgo(act?.lastLogin)}`}
                    primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                  />
                  <Chip label={`${recent} giriş`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                </ListItemButton>
              );
            })}
          </NumberedListCard>
        </Grid>
      </Grid>
      </>
      )}

      {/* İstek bazlı detay — DAU/WAU/MAU, saatlik yoğunluk, modül ısı haritası */}
      {visibleWidgets.usage && usage && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            İstek Bazlı Detay (Son 30 Gün)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sayılar şube veya şirket adedi değil; kullanıcının o işe attığı API isteğidir.
            E-ticaret sütunu hem panel kullanımını hem firmanın vitrin sitesini kapsar.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="İstek DAU (bugün)" value={usage.dau} color="#2e7d32" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="İstek WAU (7 gün)" value={usage.wau} color="#0288d1" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="İstek MAU (30 gün)" value={usage.mau} color="#7b1fa2" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="İstek Atan Şirket (bugün)" value={usage.companiesToday} color="#102648" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard title="Son 7 Gün İstek" value={usage.last7Requests} color="#ed6c02" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper
                elevation={1}
                sx={{ p: 2, borderRadius: 2, height: '100%', border: '2px solid', borderColor: '#00968820' }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  Haftalık Değişim
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: usage.wowChangePct == null ? 'text.secondary' : usage.wowChangePct >= 0 ? '#2e7d32' : '#d32f2f',
                  }}
                >
                  {usage.wowChangePct == null ? '—' : `${usage.wowChangePct > 0 ? '+' : ''}${usage.wowChangePct}%`}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            {/* Günlük istek + benzersiz kullanıcı serisi */}
            <Grid item xs={12} lg={7}>
              <Paper elevation={1} sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Günlük İstek ve Aktif Kullanıcı
                </Typography>
                <Box sx={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={usageCharts.daily} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="usageReqFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00897b" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#00897b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
                      <RechartsTooltip labelFormatter={(label) => `Tarih: ${label}`} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="requests" name="İstek" stroke="#00897b" strokeWidth={2} fill="url(#usageReqFill)" />
                      <Area type="monotone" dataKey="uniqueUsers" name="Aktif kullanıcı" stroke="#5e35b1" strokeWidth={2} fillOpacity={0} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Saatlik yoğunluk */}
            <Grid item xs={12} lg={5}>
              <Paper elevation={1} sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Saatlik Yoğunluk (giriş saati)
                </Typography>
                <Box sx={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usageCharts.hourly} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                      <RechartsTooltip labelFormatter={(label) => `Saat: ${label}:00`} />
                      <Bar dataKey="count" name="Giriş" fill="#0288d1" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Modül kullanımı: toplam + şirket x modül ısı haritası */}
          {moduleSummary.length > 0 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} lg={4}>
                <Paper elevation={1} sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    En Çok Kullanılan Modüller
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Sayı, o işe atılan API isteğidir. E-ticaret sitesi (vitrin + panel) dahildir.
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={moduleSummary.slice(0, 10).map((m) => ({
                          ...m,
                          label: usageModuleLabel(m.module),
                        }))}
                        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={110} />
                        <RechartsTooltip />
                        <Bar dataKey="totalRequests" name="İstek" fill="#7b1fa2" radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
                <Paper elevation={1} sx={{ borderRadius: 2, p: 2, height: '100%', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Şirket x Modül Kullanım Isı Haritası
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Hücre = o şirketin o modüle son 30 günde attığı istek. Şirket/şube sütunları
                    organizasyon sayısı değil; her sayfada çekilen ayar uçları olduğu için
                    haritadan çıkarıldı. E-ticaret, firmanın kullandığı vitrin sitesini de sayar.
                  </Typography>
                  <Box
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { height: 10 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.28)', borderRadius: 4 },
                      '&::-webkit-scrollbar-track': { bgcolor: 'rgba(0,0,0,0.06)' },
                    }}
                  >
                    <Box
                      component="table"
                      sx={{
                        borderCollapse: 'collapse',
                        width: 'max-content',
                        minWidth: '100%',
                        fontSize: 12,
                      }}
                    >
                      <Box component="thead">
                        <Box component="tr">
                          <Box component="th" sx={{ textAlign: 'left', p: 0.75, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', minWidth: 140 }}>
                            Şirket
                          </Box>
                          {usageCharts.topModules.map((m) => (
                            <Box component="th" key={m} sx={{ p: 0.75, textAlign: 'center', whiteSpace: 'nowrap', minWidth: 76 }}>
                              {usageModuleLabel(m)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {companyModules.slice(0, 15).map((c) => (
                          <Box component="tr" key={c.companyId}>
                            <Box
                              component="td"
                              sx={{ p: 0.75, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', minWidth: 140, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={c.companyName}
                            >
                              {c.companyName}
                            </Box>
                            {usageCharts.topModules.map((m) => {
                              const v = c.modules[m] || 0;
                              const intensity = usageCharts.maxCell > 0 ? v / usageCharts.maxCell : 0;
                              return (
                                <Box
                                  component="td"
                                  key={m}
                                  title={`${c.companyName} · ${usageModuleLabel(m)}: ${v} istek`}
                                  sx={{
                                    p: 0.75,
                                    textAlign: 'center',
                                    bgcolor: v > 0 ? `rgba(25,118,210,${0.12 + intensity * 0.7})` : 'transparent',
                                    color: intensity > 0.6 ? '#fff' : 'text.primary',
                                    borderRadius: 0.5,
                                  }}
                                >
                                  {v > 0 ? v : ''}
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

        </Box>
      )}

      {visibleWidgets.deadlines && (
      <>
      <Divider sx={{ my: 3 }} />

      {/* Süre takibi — yazarkasa / demo / sözleşme */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Süre Takibi
      </Typography>
      <Grid container spacing={2}>
        {/* Yazarkasalar */}
        <Grid item xs={12} md={6} lg={4}>
          <NumberedListCard
            title="Süresi Yaklaşan Yazarkasalar"
            color={expiredFiscal > 0 ? '#d32f2f' : '#ed6c02'}
            count={fiscalReminders.length}
            emptyText="30 gün içinde süresi dolacak yazarkasa yok."
          >
            {fiscalReminders.map((r, index) => (
              <ListItemButton key={r.key || index} onClick={() => goToReminder(r)} divider>
                <RankBadge index={index} color={r.daysRemaining < 0 ? '#d32f2f' : '#ed6c02'} />
                <ListItemText
                  primary={r.title}
                  secondary={r.subtitle || formatDateTR(r.expiryDate)}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                />
                <Chip
                  label={reminderTiming(r.daysRemaining)}
                  size="small"
                  color={r.daysRemaining < 0 ? 'error' : 'warning'}
                />
              </ListItemButton>
            ))}
          </NumberedListCard>
        </Grid>

        {/* Demolar */}
        <Grid item xs={12} md={6} lg={4}>
          <NumberedListCard
            title="Süresi Yaklaşan Demolar"
            color="#ed6c02"
            count={demoCompanies.length}
            emptyText="Demo şirket yok."
          >
            {demoCompanies.map(({ company, days }, index) => (
              <ListItemButton key={company.id} onClick={() => openDetails(company)} divider>
                <RankBadge index={index} color={days < 0 ? '#d32f2f' : '#ed6c02'} />
                <ListItemText
                  primary={company.name}
                  secondary={`Bitiş: ${formatDateTR(company.demoUntil)}`}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                />
                <Tooltip title={formatDateTR(company.demoUntil)}>
                  <Chip
                    label={days < 0 ? `${Math.abs(days)} gün geçti` : days === 0 ? 'Bugün doluyor' : `${days} gün`}
                    size="small"
                    color={days < 0 ? 'error' : days <= 7 ? 'warning' : 'default'}
                    variant={days > 7 ? 'outlined' : 'filled'}
                  />
                </Tooltip>
              </ListItemButton>
            ))}
          </NumberedListCard>
        </Grid>

        {/* Sözleşmeler */}
        <Grid item xs={12} md={12} lg={4}>
          <NumberedListCard
            title="Sözleşmesi Bitecek Şirketler"
            color="#7b1fa2"
            count={contractReminders.length}
            emptyText="30 gün içinde bitecek sözleşme yok."
          >
            {contractReminders.map((r, index) => (
              <ListItemButton key={r.key || index} onClick={() => goToReminder(r)} divider>
                <RankBadge index={index} color={r.daysRemaining < 0 ? '#d32f2f' : '#7b1fa2'} />
                <ListItemText
                  primary={r.title}
                  secondary={r.subtitle || formatDateTR(r.expiryDate)}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 500 }}
                />
                <Chip
                  label={reminderTiming(r.daysRemaining)}
                  size="small"
                  color={r.daysRemaining < 0 ? 'error' : 'warning'}
                />
              </ListItemButton>
            ))}
          </NumberedListCard>
        </Grid>
      </Grid>
      </>
      )}

      {/* Şirket detay modalı — liste satırına tıklayınca */}
      <CompanyDetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} company={detailsCompany} />
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
